import React from 'react';
import {
  Flex,
  TonicProps,
  Tooltip,
  TooltipProps,
  Truncate,
} from '@tonic-one/react';
import { useEventListener, useToggle } from '@tonic-one/react-hooks';

export interface TextEllipsisProps
  extends Omit<TooltipProps, 'label' | 'PopperProps'> {
  children: React.ReactNode;
  align?: string;
  lines?: number;
  tooltipLabel?: React.ReactNode;
  popperProps?: TooltipProps['PopperProps'];
  contentProps?: TonicProps;
}

function TextEllipsis(props: TextEllipsisProps) {
  const {
    children,
    align,
    lines,
    tooltipLabel,
    popperProps,
    placement = 'top',
    contentProps,
    ...tooltipProps
  } = props;
  const textRef = React.useRef();
  const place = placement ? placement : 'top';
  const [isOpen, toggleIsOpen] = useToggle();

  const lineClampStyle = React.useMemo(
    () =>
      !!lines && lines > 0
        ? {
            // line-clamp styles for browsers that support it otherwise
            // the Truncate styles will be used
            [`@supports (-webkit-line-clamp: ${lines})`]: {
              '--truncate-line-clamp': lines,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 'var(--truncate-line-clamp)',
            },
          }
        : undefined,
    [lines]
  );

  useEventListener(
    () => textRef.current,
    'mouseenter', // It can be either 'mouseenter' or 'pointerenter'
    React.useCallback(
      (event: any) => {
        const isOverflowing =
          event.currentTarget.offsetHeight < event.currentTarget.scrollHeight ||
          event.currentTarget.offsetWidth < event.currentTarget.scrollWidth;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isOverflowing && toggleIsOpen(true);
      },
      [toggleIsOpen]
    )
  );

  useEventListener(
    () => textRef.current,
    'mouseleave', // It can be either 'mouseleave' or 'pointerleave'
    React.useCallback(() => {
      toggleIsOpen(false);
    }, [toggleIsOpen])
  );

  return (
    <Tooltip
      isOpen={isOpen}
      label={tooltipLabel ?? children}
      placement={place}
      PopperProps={{
        maxWidth: '480px',
        wordBreak: 'break-all',
        ...popperProps,
      }}
      padding="3x"
      {...tooltipProps}
    >
      <Flex
        alignItems="center"
        columnGap="2x"
        justify={align || 'start'}
        width="100%"
      >
        <Truncate ref={textRef} {...contentProps} sx={lineClampStyle}>
          {children}
        </Truncate>
      </Flex>
    </Tooltip>
  );
}

export default TextEllipsis;
